/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _sortBy from 'lodash.sortby';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import flattenArray from 'cio/utils/flatten-array';
import { groupIdentitiesByDomain } from 'cio/utils/group-identities-by-domain';

import DS from 'ember-data';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import DomainModel from 'cio/models/domain';
import IdentityModel from 'cio/models/identity';
import MailServerModel from 'cio/models/mail-server';

interface EmailSendingDomainsArgs {
  domains: DS.RecordArray<DomainModel>;
  identities: DS.RecordArray<IdentityModel>;
  identitiesInUse: DS.RecordArray<IdentityModel>;
  mailServers: DS.RecordArray<MailServerModel>;
  editingDomain: string;
  onEdit: (domain: string) => unknown;
}

export default class EmailSendingDomains extends Component<EmailSendingDomainsArgs> {
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;

  @tracked addingDomain = false;

  get existingDomains() {
    return this.args.domains.filter((domain) => !domain.isNew);
  }
  get sortedDomains() {
    return _sortBy(this.existingDomains, ['notVerified', 'domain']);
  }
  get identitiesByDomain() {
    return groupIdentitiesByDomain(
      this.args.identities.toArray().sort((a, b) => Number(a.id) - Number(b.id)),
      this.sortedDomains,
    );
  }
  get dynamicIdentities() {
    let knownIdentities = flattenArray(Object.values(this.identitiesByDomain));
    return this.args.identities.filter((identity) => !knownIdentities.includes(identity));
  }

  @action
  async onUpdateIdentity() {
    await this.args.domains.update();
    await this.args.identities.update();
  }
  @action
  async onCreate() {
    await this.args.identities.update();
    this.addingDomain = false;
    this.alerts.success('sending-domain.created', 'Domain added');
  }

  deleteDomainTask = dropTask(async (domain: DomainModel) => {
    try {
      let identities = this.identitiesByDomain[domain.lowercasedDomain];
      await domain.destroyRecord();
      identities.forEach((identity) => identity.unloadRecord());
      this.args.onEdit('');
      this.alerts.success('sending-domain.deleted', 'Domain deleted');
    } catch (e) {
      this.apiErrors.report(e);
    }
  });
}
